import React, {
  useState,
  Suspense,
  lazy,
  createContext,
  ReactNode,
  FC,
  useCallback,
  useEffect,
  useContext,
} from "react";
import { ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  Outlet,
  useLocation,
  useNavigate
} from "react-router-dom";
import { CircularProgress, CssBaseline, PaletteMode, Box } from "@mui/material";
import { LIGHT_MODE, DARK_MODE } from "./utils/Constants";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Layouts from "./components/layout/Layouts";
import useCustomTheme from "./styles/themes";
import { ClinicOfficesProvider } from "./contexts/ClinicContext";
import { getUser } from "./utils/storage";
import { UserContext, UserProvider } from "./contexts/UserContext";
import GlobalLanguageSwitcher from "./components/common/GlobalLanguageSwitcher";
import { KioskProvider } from "./components/kiosk/KioskContext";
import { AdminProvider } from "./contexts/AdminContext";

// Account & Authentication Routes
const Login = lazy(() => import("./components/account/LoginPage"));
const Signup = lazy(() => import("./components/account/sign-up/SignUpPage"));
const DataPage = lazy(() => import("./components/account/sign-up/DataPage"));
const ActivationPage = lazy(() => import("./components/account/sign-up/ActivationPage"));
const AutoLoginPage = lazy(() => import("./components/account/AutoLoginPage"));
const SSOLoginPage = lazy(() => import("./components/account/SSOLoginPage"));
const Logout = lazy(() => import("./components/account/LogoutPage"));
const ForgotPassword = lazy(() => import("./components/account/ForgotPasswordComponent"));
const ResetPassword = lazy(() => import("./components/account/ResetPassword"));
const UserInfo = lazy(() => import("./modules/account/UserInfo"));
const AccountPage = lazy(() => import("./modules/account/AccountPage"));

// User & Clinic Management Routes
const Dashboard = lazy(() => import("./modules/dashboard/DashboardPage"));
const AnalyticsPage = lazy(() => import("./modules/analytics/AnalyticsPage"));
const ClinicOffice = lazy(() => import("./modules/management/clinic-office/ClinicOffice"));
const ClinicUser = lazy(() => import("./modules/management/clinic-user/ClinicUser"));
const AddDriver = lazy(() => import("./modules/management/add-driver/AddDriver"));
const InvitedDriver = lazy(() => import("./modules/management/add-driver/InvitedDriver"));
const Provider = lazy(() => import("./modules/management/provider/Provider"));
const ProviderActivationPage = lazy(() => import("./modules/management/clinic-user/ProviderActivationPage"));
const DocumentsDriver = lazy(() => import("./modules/documents/DocumentesDriver"));
const DriverHistory = lazy(() => import("./modules/driver/surgery-medication/DriverHistoryPage"));

// Physical Exam, Survey & NRCME Routes
const Physical = lazy(() => import("./modules/physical/PhysicalPage"));
const StartPhyscial = lazy(() => import("./modules/physical/StartPhysical"));
const Survey = lazy(() => import("./modules/survey/SurveyPage"));
const NRCME = lazy(() => import("./modules/survey/NRCME"));
const NRCMEOnboardingUI = lazy(() => import("./modules/nrcme/NRCMEOnboardingUI"));
const PreScreeningTabsManagement = lazy(() => import("./modules/survey/PreScreeningTabsManagement"));
const VitalsPage = lazy(() => import("./modules/driver/VitalsPage"));

// Forms, Documents & Resources Routes
const Help = lazy(() => import("./modules/help/Help"));
const Forms = lazy(() => import("./modules/forms/Forms"));
const Documents = lazy(() => import("./modules/documents/Documents"));
const Resources = lazy(() => import("./modules/forms/Resources"));
const ReleaseNotesPage = lazy(() => import("./components/common/ReleaseNotesPage"));

// Billing & Subscription Payment Routes
const StripeWrapper = lazy(() => import("./components/payment/StripePaymentComponent"));
const PricingPlansPage = lazy(() => import("./components/payment/PricingPlansPage"));

// Kiosk System Routes
const KioskPageManager = lazy(() => import("./components/kiosk/KioskManager"));
const KioskUnlockDialog = lazy(() => import("./components/kiosk/KioskUnlockDialog"));
const PublicKioskManager = lazy(() => import("./components/kiosk/PublicKioskManager"));

// Admin Portal Routes
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./components/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./components/admin/AdminUsers"));
const AdminClinics = lazy(() => import("./components/admin/AdminClinics"));
const AdminReports = lazy(() => import("./components/admin/AdminReports"));
const AdminTransactions = lazy(() => import("./components/admin/AdminTransactions"));
const AdminSettings = lazy(() => import("./components/admin/AdminSettings"));
const AdminResources = lazy(() => import("./components/admin/AdminResources"));
const AdminQuestionnaire = lazy(() => import("./components/admin/AdminQuestionnaire"));
const AdminMedications = lazy(() => import("./components/admin/AdminMedications"));
const AdminSurgeries = lazy(() => import("./components/admin/AdminSurgeries"));
const AdminCampaigns = lazy(() => import("./components/admin/AdminCampaigns"));

// Error & Fallback Routes
const ErrorPage = lazy(() => import("./components/error/ErrorPage"));
const NotAuthorizedPage = lazy(() => import("./components/error/NotAuthorizedComponent"));

// Dashboard Context
interface DashboardContextType {
  dashboardData: any;
  setDashboardData: React.Dispatch<React.SetStateAction<any>>;
}
const DashboardContext = createContext<DashboardContextType | null>(null);
const DashboardProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [dashboardData, setDashboardData] = useState<any>(null);
  return (
    <DashboardContext.Provider value={{ dashboardData, setDashboardData }}>
      {children}
    </DashboardContext.Provider>
  );
};

// Message Context
interface MessageContextType {
  messages: string[];
  addMessage: (message: string) => void;
  removeMessage: (index: number) => void;
}
const MessageContext = createContext<MessageContextType | null>(null);
const MessageProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [messages, setMessages] = useState<string[]>([]);
  const addMessage = useCallback(
    (message: string) => setMessages((prev) => [...prev, message]),
    []
  );

  const removeMessage = useCallback(
    (index: number) => setMessages((prev) => prev.filter((_, i) => i !== index)),
    []
  );
  return (
    <MessageContext.Provider value={{ messages, addMessage, removeMessage }}>
      {children}
    </MessageContext.Provider>
  );
};

// Private Route
interface PrivateRouteProps {
  children: ReactNode;
  allowedRoles?: string[];
}
const PrivateRoute: FC<PrivateRouteProps> = ({
  allowedRoles = [],
  children,
}) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isKioskLocked = localStorage.getItem("isKioskMode") === "true";

  if (loading) return null;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isKioskLocked && location.pathname !== "/kiosk-mode") {
    return (
      <KioskUnlockDialog
        onSuccess={() => navigate(location.pathname)}
        onCancel={() => navigate("/kiosk-mode")}
      />
    );
  }

  if (!user) {
    return null;
  }

  // If user only has Admin role, redirect from non-admin routes
  if (hasOnlyAdminRole(user) && !location.pathname.startsWith("/admin-section")) {
    return <Navigate to="/admin-section/dashboard" replace />;
  }

  const hasClinicRole = user?.roles?.some(
    (role: any) => role.name === "Clinic"
  );

  const hasDriverRole = user?.roles?.some(
    (role: any) => role.name?.toLowerCase() === "driver"
  );

  if (user.first_login) {
    if (hasDriverRole) {
      // Drivers gather all demographic info inside the physical survey module natively.
      // We do not force them into the generic /user-info page.
    } else if (location.pathname !== "/user-info") {
      return <Navigate to="/user-info" replace />;
    }
  }

  if (
    !user.first_login &&
    hasClinicRole &&
    user.subscription_status !== "active" &&
    location.pathname !== "/subscription" &&
    location.pathname !== "/plans"
  ) {
    return <Navigate to="/plans" replace />;
  }

  // 3. Role-based access
  if (allowedRoles.length) {
    const hasRole = allowedRoles.some((role) =>
      user?.roles?.some((userRole: { name: string }) => userRole.name?.toLowerCase() === role.toLowerCase())
    );

    if (!hasRole) {
      return <Navigate to="/not-authorized" replace />;
    }

    // Strict Admin check: If accessing /admin-section, user must ONLY have the Admin role
    if (allowedRoles.some(r => r.toLowerCase() === "admin") && location.pathname.startsWith("/admin-section") && !hasOnlyAdminRole(user)) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

const isAdminUser = (user: any) => {
  return user?.roles?.some((role: any) => role.name?.toLowerCase() === "admin");
};

const hasOnlyAdminRole = (user: any) => {
  const roles = user?.roles || [];
  if (roles.length === 0) return false;

  const isAdmin = roles.some((role: any) => role.name?.toLowerCase() === "admin");
  const hasOtherRoles = roles.some((role: any) => role.name?.toLowerCase() !== "admin");

  return isAdmin && !hasOtherRoles;
};

interface PublicRouteProps {
  children: ReactNode;
}
const PublicRoute: FC<PublicRouteProps> = ({ children }) => {
  const { user } = useContext(UserContext) || {};
  const { isAuthenticated, loading } = useAuth();

  if (loading) return null;

  if (isAuthenticated) {
    if (hasOnlyAdminRole(user)) {
      return <Navigate to="/admin-section/dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

const ContextProviders: FC<{ children: ReactNode }> = ({ children }) => (
  <AuthProvider>
    <UserProvider>
      <AdminProvider>
        <DashboardProvider>
          <MessageProvider>{children}</MessageProvider>
        </DashboardProvider>
      </AdminProvider>
    </UserProvider>
  </AuthProvider>
);

const DocsBotInitializer = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const user = getUser();
    const widget = document.getElementById("docsbotai-root");

    if (isAuthenticated && user && user.roles) {
      const roles = user.roles.map((r: any) => r.name?.toLowerCase());
      const isAllowed = roles.includes("clinic") || roles.includes("provider") || roles.includes("medical assistant");
      const isKioskPage = location.pathname.toLowerCase().includes("kiosk");

      if (isAllowed && !isKioskPage) {
        if (widget) widget.style.display = 'block';

        if ((window as any).DocsBotAI && (window as any).DocsBotAI.init && !(window as any).docsBotInitialized) {
          (window as any).DocsBotAI.init({ id: "0rvBjhsg7tXSSKutfe59/Y29QfiP6BjPwooUTSGDF" }).catch(console.error);
          (window as any).docsBotInitialized = true;
        }
      } else {
        if (widget) widget.style.display = 'none';
      }
    } else {
      if (widget) widget.style.display = 'none';
    }
  }, [isAuthenticated, location.pathname]);

  return null;
};

const AppLayout = () => (
  <>
    <DocsBotInitializer />
    <GlobalLanguageSwitcher />
    <Outlet />
  </>
);

const initializeThemeMode = (): PaletteMode => {
  const savedMode = localStorage.getItem("themeMode") as PaletteMode | null;
  const validMode = savedMode === LIGHT_MODE || savedMode === DARK_MODE;
  const themeMode = validMode ? savedMode : LIGHT_MODE;
  if (!validMode) localStorage.setItem("themeMode", LIGHT_MODE);
  return themeMode;
};

const App = () => {
  const [theme, setTheme] = useState<PaletteMode>(initializeThemeMode());
  const gmTheme = useCustomTheme(theme);
  const user = getUser() || {};
  const appRouter = createBrowserRouter([
    {
      path: "/",
      element: <AppLayout />,
      children: [
        {
          path: "/",
          element: (
            <Navigate
              to={
                user?.first_login
                  ? user?.roles?.some((role: any) => role.name?.toLowerCase() === "driver") && user?.is_invited
                    ? "/physical?autoStart=true"
                    : "/user-info"
                  : hasOnlyAdminRole(user)
                    ? "/admin-section/dashboard"
                    : "/dashboard"
              }
              replace
            />
          ),
        },
        { path: "/activate/:token", element: <ProviderActivationPage /> },
        {
          path: "/login",
          element: (
            <PublicRoute>
              <Login />
            </PublicRoute>
          ),
        },
        {
          path: "/signup/",
          element: (
            <PublicRoute>
              <Signup />
            </PublicRoute>
          ),
        },
        {
          path: "/signup/:role",
          element: (
            <PublicRoute>
              <Signup />
            </PublicRoute>
          ),
        },
        { path: "/logout", element: <Logout /> },
        { path: "/auto-login/:token", element: <AutoLoginPage /> },
        { path: "/sso-login", element: <SSOLoginPage /> },
        { path: "/signup/activate/:token", element: <ActivationPage /> },
        { path: "/thankyou/:tokenData", element: <DataPage /> },
        { path: "/forgot-password", element: <ForgotPassword /> },
        { path: "/reset-password/:token", element: <ResetPassword /> },
        { path: "/kiosk-mode", element: <KioskProvider><KioskPageManager /></KioskProvider> },
        { path: "/kiosk/register/:clinicId", element: <PublicKioskManager /> },
        { path: "/not-authorized", element: <NotAuthorizedPage /> },
        {
          path: "/user-info",
          element: (
            <PrivateRoute>
              <UserInfo />
            </PrivateRoute>
          ),
        },
        {
          path: "/subscription",
          element: (
            <PrivateRoute>
              <StripeWrapper />
            </PrivateRoute>
          ),
        },
        {
          path: "/plans",
          element: (
            <PrivateRoute>
              <PricingPlansPage />
            </PrivateRoute>
          ),
        },
        // Admin Routes
        {
          path: "/admin-section",
          element: (
            <PrivateRoute allowedRoles={["Admin"]}>
              <AdminLayout>
                <Outlet />
              </AdminLayout>
            </PrivateRoute>
          ),
          children: [
            { path: "dashboard", element: <AdminDashboard /> },
            { path: "users", element: <AdminUsers /> },
            { path: "clinics", element: <AdminClinics /> },
            { path: "reports", element: <AdminReports /> },
            { path: "transactions", element: <AdminTransactions /> },
            { path: "settings", element: <AdminSettings /> },
            { path: "resources", element: <AdminResources /> },
            { path: "questionnaire", element: <AdminQuestionnaire /> },
            { path: "medications", element: <AdminMedications /> },
            { path: "surgeries", element: <AdminSurgeries /> },
            { path: "campaigns", element: <AdminCampaigns /> },
            { index: true, element: <Navigate to="dashboard" replace /> },
          ],
        },

        {
          element: (
            <PrivateRoute>
              <ClinicOfficesProvider>
                <Layouts>
                  <Outlet />
                </Layouts>
              </ClinicOfficesProvider>
            </PrivateRoute>
          ),
          children: [
            {
              path: "/dashboard",
              element: <Dashboard />,
            },
            {
              path: "/account",
              element: (
                <PrivateRoute>
                  <AccountPage />
                </PrivateRoute>
              ),
            },
            {
              path: "/account/change-password",
              element: <ResetPassword />,
            },
            { path: "/nrcme", element: <NRCME /> },
            { path: "/help", element: <Help /> },
            { path: "/forms", element: <Forms /> },
            { path: "/resources", element: <Resources /> },
            { path: "/release-notes", element: <ReleaseNotesPage /> },
            { path: "/physical", element: <StartPhyscial /> },
            { path: "/vitals", element: <VitalsPage /> },
            { path: "/nrcme-setup", element: <NRCMEOnboardingUI /> },

            { path: "/documents", element: <Documents /> },
            { path: "/documents-driver", element: <DocumentsDriver /> },
            { path: "/survey/:surveyId", element: <Survey /> },
            { path: "/pre-screening/:surveyId", element: <PreScreeningTabsManagement /> },
            { path: "user-history", element: <DriverHistory /> },
            {
              path: "/user",
              children: [
                {
                  path: "analytics",
                  element: (
                    <PrivateRoute allowedRoles={["Clinic"]}>
                      <AnalyticsPage />
                    </PrivateRoute>
                  ),
                },
                {
                  path: "clinic-office",
                  element: (
                    <PrivateRoute allowedRoles={[]}>
                      <ClinicOffice />
                    </PrivateRoute>
                  ),
                },
                { path: "clinic-user", element: <ClinicUser /> },
                { path: "provider", element: <Provider /> },
                { path: "add-driver", element: <AddDriver /> },
                { path: "invited-driver", element: <InvitedDriver /> },
              ],
            },
          ],
        },
      ],
      errorElement: <ErrorPage />,
    },
  ]);

  return (
    <ThemeProvider theme={gmTheme}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <CssBaseline />
        <ContextProviders>
          <Suspense
            fallback={
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "100vh",
                  width: "100%",
                }}
              >
                <CircularProgress />
              </Box>
            }
          >
            <RouterProvider router={appRouter} />
          </Suspense>
        </ContextProviders>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default App;
